import Icon from '@amzn/awsui-components-core/icon';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import useEmblaCarousel from 'embla-carousel-react';
import Autoplay from 'embla-carousel-autoplay';
import { NflGame } from 'src/services/NflScoresService';
import { NFL_ESPN_URL } from '../utils/welcome-banner-utils';

/** Embla plugin events aren't included in core EmblaEventType — cast needed for autoplay events. */
type AutoplayEvent = 'autoplay:play' | 'autoplay:stop' | 'autoplay:timerset' | 'autoplay:timerstopped';

interface NflScoresCardProps {
  games: NflGame[];
}

/** Returns true if the given date is the same calendar day as today in user's local timezone. */
const isToday = (date: Date): boolean => {
  const now = new Date();
  return date.getFullYear() === now.getFullYear() && date.getMonth() === now.getMonth() && date.getDate() === now.getDate();
};

/** Builds consolidated label: state + detail in one line. Kept short to fit the 220px card. */
const buildLabel = (game: NflGame): string => {
  if (game.state === 'in') {
    return `NFL · 🔴 ${game.statusDetail}`;
  }
  if (game.state === 'pre') {
    const kickoffDate = new Date(game.startTime);
    // Fall back to ESPN's own status string if the ISO date is missing or unparseable.
    if (Number.isNaN(kickoffDate.getTime())) {
      return game.statusDetail ? `NFL · ${game.statusDetail}` : 'NFL · Upcoming';
    }
    const time = kickoffDate.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
    if (isToday(kickoffDate)) {
      return `NFL · ${time}`;
    }
    const day = kickoffDate.toLocaleDateString([], { month: 'short', day: 'numeric' });
    return `NFL · ${day} ${time}`;
  }
  return 'NFL · Final';
};

/**
 * @feature landing-page
 * @description NFL live scores card — uses Embla Carousel with autoplay.
 * Shows: smooth sliding transitions, prev/next arrows, play/stop toggle, progress bar.
 */
const NflScoresCard: React.FC<NflScoresCardProps> = ({ games }) => {
  const progressRef = useRef<HTMLDivElement>(null);
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true }, [Autoplay({ delay: 8000, playOnInit: true, stopOnInteraction: false })]);
  const [isPlaying, setIsPlaying] = useState(true);
  const [showProgress, setShowProgress] = useState(false);
  const animationName = useRef('');

  // Sync autoplay state from Embla events
  useEffect(() => {
    if (!emblaApi) return;
    const autoplay = emblaApi.plugins().autoplay;
    if (!autoplay) return;

    setIsPlaying(autoplay.isPlaying());

    const onPlay = () => setIsPlaying(true);
    const onStop = () => setIsPlaying(false);

    emblaApi.on('autoplay:play' as AutoplayEvent, onPlay).on('autoplay:stop' as AutoplayEvent, onStop);
    return () => {
      emblaApi.off('autoplay:play' as AutoplayEvent, onPlay).off('autoplay:stop' as AutoplayEvent, onStop);
    };
  }, [emblaApi]);

  // Progress bar animation — restarts on each timer set
  useEffect(() => {
    if (!emblaApi) return;
    const autoplay = emblaApi.plugins().autoplay;
    if (!autoplay) return;

    const startProgress = () => {
      const node = progressRef.current;
      const timeUntilNext = autoplay.timeUntilNext();
      if (!node || timeUntilNext === null) return;

      if (!animationName.current) {
        const style = window.getComputedStyle(node);
        animationName.current = style.animationName || 'autoplay-progress';
      }

      node.style.animationName = 'none';
      node.style.transform = 'translate3d(0,0,0)';

      requestAnimationFrame(() => {
        setTimeout(() => {
          node.style.animationName = animationName.current;
          node.style.animationDuration = `${timeUntilNext}ms`;
        }, 0);
      });

      setShowProgress(true);
    };

    const stopProgress = () => setShowProgress(false);

    emblaApi.on('autoplay:timerset' as AutoplayEvent, startProgress).on('autoplay:timerstopped' as AutoplayEvent, stopProgress);
    return () => {
      emblaApi.off('autoplay:timerset' as AutoplayEvent, startProgress).off('autoplay:timerstopped' as AutoplayEvent, stopProgress);
    };
  }, [emblaApi]);

  const onAutoplayButtonClick = useCallback(
    (callback: () => void) => {
      const autoplay = emblaApi?.plugins()?.autoplay;
      if (autoplay) autoplay.stop();
      callback();
    },
    [emblaApi]
  );

  const onPrevClick = useCallback(() => {
    if (!emblaApi) return;
    onAutoplayButtonClick(() => emblaApi.scrollPrev());
  }, [emblaApi, onAutoplayButtonClick]);

  const onNextClick = useCallback(() => {
    if (!emblaApi) return;
    onAutoplayButtonClick(() => emblaApi.scrollNext());
  }, [emblaApi, onAutoplayButtonClick]);

  const toggleAutoplay = useCallback(() => {
    const autoplay = emblaApi?.plugins()?.autoplay;
    if (!autoplay) return;
    if (autoplay.isPlaying()) {
      autoplay.stop();
    } else {
      autoplay.play();
    }
  }, [emblaApi]);

  if (games.length === 0) return null;
  const showControls = games.length > 1;

  return (
    <div className="landing-welcome-banner__card landing-welcome-banner__nfl-card" data-testid="nfl-card">
      <a
        className="landing-welcome-banner__card-link"
        href={NFL_ESPN_URL}
        target="_blank"
        rel="noopener noreferrer"
        aria-label="View NFL Scoreboard on ESPN"
      >
        <Icon name="external" size="small" />
      </a>
      <span className="landing-welcome-banner__card-icon">🏈</span>
      <div className="landing-welcome-banner__card-content">
        <div className="landing-welcome-banner__nfl-viewport" ref={emblaRef}>
          <div className="landing-welcome-banner__nfl-container">
            {games.map((game) => (
              <div className="landing-welcome-banner__nfl-slide" key={game.id}>
                <span className="landing-welcome-banner__card-label">{buildLabel(game)}</span>
                <span className="landing-welcome-banner__card-value">
                  {game.state === 'pre'
                    ? `${game.awayTeam} @ ${game.homeTeam}`
                    : `${game.awayTeam} ${game.awayScore} - ${game.homeTeam} ${game.homeScore}`}
                </span>
              </div>
            ))}
          </div>
        </div>

        {showControls && (
          <div className="landing-welcome-banner__nfl-controls">
            <div className="landing-welcome-banner__nfl-buttons">
              <button className="landing-welcome-banner__nfl-btn" onClick={onPrevClick} aria-label="Previous game" data-testid="nfl-prev-btn">
                <svg viewBox="0 0 532 532" className="landing-welcome-banner__nfl-btn-svg">
                  <path
                    fill="currentColor"
                    d="M355.66 11.354c13.793-13.805 36.208-13.805 50.001 0 13.785 13.804 13.785 36.238 0 50.034L201.22 266l204.442 204.61c13.785 13.805 13.785 36.239 0 50.044-13.793 13.796-36.208 13.796-50.002 0a5994246.277 5994246.277 0 0 0-229.332-229.454 35.065 35.065 0 0 1-10.326-25.126c0-9.2 3.393-18.26 10.326-25.2C172.192 194.973 332.731 34.31 355.66 11.354Z"
                  />
                </svg>
              </button>
              <button className="landing-welcome-banner__nfl-btn" onClick={onNextClick} aria-label="Next game" data-testid="nfl-next-btn">
                <svg viewBox="0 0 532 532" className="landing-welcome-banner__nfl-btn-svg">
                  <path
                    fill="currentColor"
                    d="M176.34 520.646c-13.793 13.805-36.208 13.805-50.001 0-13.785-13.804-13.785-36.238 0-50.034L330.78 266 126.34 61.391c-13.785-13.805-13.785-36.239 0-50.044 13.793-13.796 36.208-13.796 50.002 0 22.928 22.947 206.395 206.507 229.332 229.454a35.065 35.065 0 0 1 10.326 25.126c0 9.2-3.393 18.26-10.326 25.2-45.865 45.901-206.404 206.564-229.332 229.52Z"
                  />
                </svg>
              </button>
            </div>

            <div className={`landing-welcome-banner__nfl-progress${showProgress ? '' : ' landing-welcome-banner__nfl-progress--hidden'}`}>
              <div className="landing-welcome-banner__nfl-progress-bar" ref={progressRef} />
            </div>

            <button
              className="landing-welcome-banner__nfl-play"
              onClick={toggleAutoplay}
              aria-label={isPlaying ? 'Pause auto-rotation' : 'Play auto-rotation'}
              data-testid="nfl-play-pause-btn"
            >
              {isPlaying ? (
                <svg viewBox="0 0 24 24" className="landing-welcome-banner__nfl-play-svg">
                  <rect x="6" y="4" width="4" height="16" rx="1" fill="currentColor" />
                  <rect x="14" y="4" width="4" height="16" rx="1" fill="currentColor" />
                </svg>
              ) : (
                <svg viewBox="0 0 24 24" className="landing-welcome-banner__nfl-play-svg">
                  <path d="M8 5v14l11-7z" fill="currentColor" />
                </svg>
              )}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default NflScoresCard;
